import {
  ArrowLeftRight, Box, Boxes, CalendarClock, CalendarDays, ClipboardCheck, ClipboardList, FileSearch, Flame, Gauge,
  LayoutDashboard, LayoutGrid, ListChecks, MapPin, Package, PackageCheck, PackageOpen, PackagePlus, PackageSearch,
  Plug, ReceiptText, RefreshCcw, ScrollText, Search, Settings, ShoppingCart, Store, TrendingUp,
  TriangleAlert, Truck, Undo2, Upload, Users, Warehouse, Wrench, Zap,
  type LucideIcon,
} from "lucide-react";
import { temPapel, type Papel, type Perfil } from "./auth";
import { podePedidoManual } from "./pedidosManuais";

/** Todas as telas do WMS, com quem pode usar cada uma. Base do início e do menu. */
export interface ItemMenu {
  to: string;
  icone: LucideIcon;
  titulo: string;
  /** descrição curta: só no título (dica) do botão, a tela mostra ícone e nome */
  texto: string;
  papeis: Papel[];
  grupo: GrupoMenu;
  /** atalho grande no início para quem tem este papel como principal */
  destaque?: Papel[];
  /** nome nos atalhos do cabeçalho, quando o título não cabe */
  curto?: string;
  /** fica fora do início e do menu (aberta de outra tela); a rota e o esconder por função continuam valendo */
  semMenu?: boolean;
}

export type GrupoMenu = "Operação" | "Entrada" | "Estoque" | "Gestão" | "Cadastros" | "Admin";

export const GRUPOS: GrupoMenu[] = ["Operação", "Entrada", "Estoque", "Gestão", "Cadastros", "Admin"];

export const ITENS_MENU: ItemMenu[] = [
  { to: "/separacao", icone: ShoppingCart, titulo: "Pré-separação", texto: "Pegar onda e coletar no carrinho, posição por posição",
    papeis: ["separador", "lider"], grupo: "Operação", destaque: ["separador", "lider"] },
  { to: "/montagem", icone: Boxes, titulo: "Separação", texto: "Na esteira, pedido por pedido: bipar a caixa preta e os itens",
    papeis: ["separador", "lider"], grupo: "Operação", destaque: ["separador"] },
  { to: "/conferencia", icone: ClipboardCheck, titulo: "Conferência", texto: "Bancada: caixa preta, conferência cega, DANFE e etiqueta",
    papeis: ["conferente", "lider"], grupo: "Operação", destaque: ["conferente", "lider"] },
  { to: "/expedicao", icone: Truck, titulo: "Expedição", texto: "Romaneio, carregamento dos volumes e assinatura do motorista",
    papeis: ["expedidor", "lider", "cx"], grupo: "Operação", destaque: ["expedidor"] },
  { to: "/expedicao/agenda", icone: CalendarDays, titulo: "Agenda de coletas", texto: "Coletas do Full por dia, com o preparo, e as transportadoras de hoje",
    papeis: ["expedidor", "lider", "cx"], grupo: "Operação", destaque: ["expedidor"] },
  { to: "/estoque/reposicao", icone: RefreshCcw, titulo: "Reposição", texto: "Repor picking e packing a partir do pulmão",
    papeis: ["estoquista", "lider"], grupo: "Operação", destaque: ["estoquista"] },
  { to: "/estoque/contagem", icone: ListChecks, titulo: "Contagens pedidas", texto: "Posições que o líder mandou contar (inventário e falta na coleta), contagem cega",
    papeis: ["estoquista", "lider"], grupo: "Operação", destaque: ["estoquista"] },
  { to: "/producao", icone: Wrench, titulo: "Produção", texto: "Montar caixas prontas de cadeira",
    papeis: ["separador", "estoquista", "lider"], grupo: "Operação" },
  { to: "/full", icone: Zap, titulo: "Envios Full", texto: "Mercado Livre, Shopee e Amazon: separação, etiqueta do produto e coleta",
    papeis: ["separador", "conferente", "expedidor", "lider"], grupo: "Operação" },

  { to: "/recebimento/agenda", icone: CalendarClock, titulo: "Agenda de recebimento", texto: "Ordens de compra do Tiny: o que vai chegar e quando",
    papeis: ["estoquista", "lider"], grupo: "Entrada" },
  { to: "/recebimento", icone: PackageOpen, titulo: "Recebimento", texto: "Nota do fornecedor e contagem cega: o que bate fica na doca para guardar",
    papeis: ["estoquista", "lider"], grupo: "Entrada", destaque: ["estoquista"] },
  { to: "/recebimento/armazenar", icone: Warehouse, titulo: "Armazenagem", texto: "Guardar nas posições o que está na doca (recebimento) e na quarentena (devolução boa), com sugestão de onde guardar",
    papeis: ["estoquista", "lider"], grupo: "Entrada", destaque: ["estoquista"] },
  { to: "/devolucoes", icone: Undo2, titulo: "Devoluções", texto: "Avisar o que volta e acompanhar: peso, condição por componente e NF",
    papeis: ["estoquista", "lider", "cx"], grupo: "Entrada", destaque: ["cx"] },
  { to: "/devolucoes/receber", icone: PackageCheck, titulo: "Receber devolução", texto: "Caixa chegou: bipar o rastreio, pesar e dizer a condição",
    papeis: ["estoquista", "lider"], grupo: "Entrada", destaque: ["estoquista"] },

  { to: "/estoque", icone: Search, titulo: "Consultar estoque", curto: "Estoque", texto: "Onde está cada produto e o que tem em cada posição",
    papeis: ["estoquista", "separador", "lider"], grupo: "Estoque" },
  { to: "/estoque/inventario", icone: ClipboardList, titulo: "Contar posição", texto: "Contar uma posição agora, sem pedido do líder, e acertar o saldo",
    papeis: ["estoquista", "lider"], grupo: "Estoque" },
  { to: "/estoque/entrada", icone: PackagePlus, titulo: "Entrada avulsa", texto: "Estoque que entra sem nota (acerto, achado, carga inicial), bipando ou digitando o SKU",
    papeis: ["estoquista", "lider"], grupo: "Estoque", destaque: ["lider"] },
  // aba "Digitando o SKU" da Entrada avulsa
  { to: "/estoque/adicionar", icone: Boxes, titulo: "Entrada avulsa (digitando o SKU)", texto: "Digitar o SKU, escolher a posição e a quantidade, várias linhas de uma vez",
    papeis: ["estoquista", "lider"], grupo: "Estoque", semMenu: true },
  { to: "/estoque/transferir", icone: ArrowLeftRight, titulo: "Mover estoque", texto: "De uma posição ou carrinho para outra posição: repor à mão, juntar posições, devolver a sobra do carrinho",
    papeis: ["estoquista", "separador", "lider"], grupo: "Estoque" },
  { to: "/estoque/ocupacao", icone: LayoutGrid, titulo: "Ocupação", texto: "Posições vazias, cheias e bloqueadas; juntar posições",
    papeis: ["estoquista", "lider"], grupo: "Estoque" },
  { to: "/estoque/canais", icone: Store, titulo: "Estoque por canal", texto: "O que cada canal pediu na fila e quanto sobra livre",
    papeis: ["estoquista", "lider"], grupo: "Estoque" },
  { to: "/estoque/mapa-calor", icone: Flame, titulo: "Mapa de calor", texto: "Posições mais visitadas e produtos que mais saem",
    papeis: ["lider"], grupo: "Estoque" },

  { to: "/painel", icone: LayoutDashboard, titulo: "Painel", texto: "Fila, ondas, produtividade e alertas em tempo real",
    papeis: ["lider"], grupo: "Gestão", destaque: ["lider"] },
  { to: "/pedidos", icone: ReceiptText, titulo: "Pedidos", texto: "Acompanhar, cancelar, devolver e reimprimir",
    papeis: ["lider", "conferente", "expedidor", "cx"], grupo: "Gestão", destaque: ["cx"] },
  { to: "/expedicao/consulta", icone: PackageSearch, titulo: "Consultar envio", texto: "Achar o pedido e ver quando saiu: romaneio, motorista e assinatura",
    papeis: ["cx", "expedidor", "lider"], grupo: "Gestão", destaque: ["cx"] },
  { to: "/painel/divergencias", icone: TriangleAlert, titulo: "Divergências", texto: "Resolver faltas e sobras da conferência",
    papeis: ["lider"], grupo: "Gestão" },
  { to: "/painel/desempenho", icone: TrendingUp, titulo: "Desempenho", texto: "Erros e produção por separador e conferente no período",
    papeis: ["lider"], grupo: "Gestão" },
  { to: "/inventarios", icone: FileSearch, titulo: "Inventários", texto: "Pedir contagem por posição, SKU ou cíclica e aprovar ajustes",
    papeis: ["lider"], grupo: "Gestão" },
  { to: "/cadastros/reposicao", icone: Gauge, titulo: "Mínimo e máximo", texto: "Quanto cada posição de picking e packing deve ter",
    papeis: ["lider"], grupo: "Gestão" },

  { to: "/cadastros/locais", icone: MapPin, titulo: "Posições, carrinhos e caixas", texto: "Cadastro e etiquetas de posições, carrinhos e caixas pretas",
    papeis: ["lider"], grupo: "Cadastros" },
  { to: "/cadastros/produtos", icone: Package, titulo: "Produtos", texto: "Embalagens, e-book, códigos alternativos e sync com o Tiny",
    papeis: ["lider"], grupo: "Cadastros" },
  { to: "/cadastros/lotes", icone: Boxes, titulo: "Lotes", texto: "Cadastro de lotes com produtos e impressão de etiquetas",
    papeis: ["lider"], grupo: "Cadastros" },
  { to: "/cadastros/embalagens", icone: Box, titulo: "Embalagens", texto: "Caixas de papelão e quantas cadeiras cabem em cada uma",
    papeis: ["lider"], grupo: "Cadastros" },
  { to: "/cadastros/carga", icone: Upload, titulo: "Carga inicial", texto: "Importar o estoque por posição de uma planilha",
    papeis: ["lider"], grupo: "Cadastros" },

  { to: "/admin/usuarios", icone: Users, titulo: "Usuários", texto: "Quem acessa, com qual função e quais áreas",
    papeis: ["admin"], grupo: "Admin" },
  { to: "/admin/integracao", icone: Plug, titulo: "Integração Tiny", texto: "Credenciais, conexão, webhook e rotinas automáticas",
    papeis: ["admin"], grupo: "Admin" },
  { to: "/admin/configuracoes", icone: Settings, titulo: "Configurações", texto: "Regras da operação, impressão e horários de coleta",
    papeis: ["admin", "lider"], grupo: "Admin" },
  { to: "/admin/registros", icone: ScrollText, titulo: "Registros", texto: "Erros das telas e alterações nos cadastros e permissões",
    papeis: ["admin"], grupo: "Admin" },
];

/** Telas de chão: coluna estreita, uma tarefa por vez (coletora). */
export const TELAS_COLETORA = [
  "/separacao", "/montagem", "/estoque/inventario", "/estoque/entrada", "/estoque/transferir", "/estoque/reposicao",
  "/estoque/contagem", "/recebimento/armazenar", "/devolucoes/receber",
];

/** Telas de bancada (notebook touch): largura total, botões grandes. */
export const TELAS_BANCADA = ["/conferencia"];

/** Item do menu da rota aberta (o mais específico: /expedicao/agenda antes de /expedicao). */
export function itemDaRota(caminho: string): ItemMenu | undefined {
  return ITENS_MENU
    .filter((i) => caminho === i.to || caminho.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/** A função da pessoa não escondeu a tela (tela Usuários, aba Funções). O admin vê tudo. */
export function telaVisivel(perfil: Perfil | null, to: string): boolean {
  if (!perfil || perfil.papel === "admin") return true;
  if (to === "/pedidos" && perfil.pedidos_manuais_personalizado && podePedidoManual(perfil, "consultar")) return true;
  return !(perfil.telas_ocultas ?? []).includes(to);
}

export function itemPermitido(perfil: Perfil | null, item: ItemMenu): boolean {
  return (temPapel(perfil, ...item.papeis) || (item.to === "/pedidos" && podePedidoManual(perfil, "consultar")))
    && telaVisivel(perfil, item.to);
}
